/**
 * Web venue-hours surface: re-exports shared Zurich/spillover logic + React tick.
 * Canonical implementation: `@pulse/shared` `venueHours.ts`.
 */
export {
  type VenueOpenStatus,
  type VenueHoursCategory,
  minutesToTime24,
  formatRangeLabel,
  parseRangeString,
  parseHoursString,
  isOpenNow,
  isOpenToday,
  getVenueOpenStatus,
  getVenueHoursDisplay,
  getTodayOpeningMinutes,
  getTodayClosingMinutes,
  isVenueOpenAtMinutes,
} from "@pulse/shared";

import { useEffect, useState } from "react";

/**
 * Shared minute tick — one interval for the whole app.
 *
 * VenueCard used to mount its own setInterval per instance (N timers for N
 * cards). One shared interval fans out to listeners instead.
 */
type VenueHoursTickListener = () => void;

let sharedVenueHoursNow = new Date();
const venueHoursTickListeners = new Set<VenueHoursTickListener>();
let venueHoursTickIntervalId: ReturnType<typeof setInterval> | null = null;

function startSharedVenueHoursTick(intervalMs: number) {
  if (venueHoursTickIntervalId != null) return;
  venueHoursTickIntervalId = setInterval(() => {
    sharedVenueHoursNow = new Date();
    for (const listener of venueHoursTickListeners) listener();
  }, intervalMs);
}

function stopSharedVenueHoursTickIfIdle() {
  if (venueHoursTickListeners.size > 0) return;
  if (venueHoursTickIntervalId == null) return;
  clearInterval(venueHoursTickIntervalId);
  venueHoursTickIntervalId = null;
}

/** Current time, refreshed every minute so UI recomputes open/closed status. */
export function useVenueHoursTick(intervalMs = 60_000): Date {
  const [now, setNow] = useState<Date>(() => sharedVenueHoursNow);

  useEffect(() => {
    const listener = () => setNow(sharedVenueHoursNow);
    venueHoursTickListeners.add(listener);
    startSharedVenueHoursTick(intervalMs);
    setNow(sharedVenueHoursNow);

    return () => {
      venueHoursTickListeners.delete(listener);
      stopSharedVenueHoursTickIfIdle();
    };
  }, [intervalMs]);

  return now;
}

/** Test/debug helper — how many components are subscribed to the shared tick. */
export function getVenueHoursTickListenerCountForTests(): number {
  return venueHoursTickListeners.size;
}

/** Test/debug helper — whether the shared interval is running. */
export function isVenueHoursTickRunningForTests(): boolean {
  return venueHoursTickIntervalId != null;
}

if (typeof window !== "undefined") {
  (
    window as Window & {
      __pulseVenueHoursTick?: {
        listenerCount: () => number;
        running: () => boolean;
      };
    }
  ).__pulseVenueHoursTick = {
    listenerCount: getVenueHoursTickListenerCountForTests,
    running: isVenueHoursTickRunningForTests,
  };
}
